<template>
  <div class="search">
    <v-container class="ma-0 pa-0">
      <v-row no-gutters>
        <app-select
          :label="selectedSearchLabel ? selectedSearchLabel : '検索項目'"
          :items="searchList"
          :item-props="itemProps"
          single-line
          return-object
          v-model="selected_search"
          :hide-details="true"
        />

        <v-col class="pa-0">
          <template v-if="selected_search">
            <validation-observer v-slot="{ handleSubmit }" as="div">
              <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
                <template v-if="selected_search.type === 'text'">
                  <slot
                    name="input(text)"
                    :option="selected_search"
                    :form="search_form"
                  >
                    <app-form-input
                      v-model="
                        search_form[selected_search.key] as string | number
                      "
                      variant="outlined"
                      :hide-details="true"
                    />
                  </slot>
                </template>
                <template v-else-if="selected_search.type === 'select'">
                  <slot
                    name="input(select)"
                    :option="selected_search"
                    :form="search_form"
                  >
                    <app-select
                      variant="outlined"
                      v-model="search_form[selected_search.key]"
                      :items="selectItems"
                      @update:modelValue="submit"
                      single-line
                      item-title="text"
                      item-value="value"
                      :hide-details="true"
                      :hide-no-data="true"
                    />
                  </slot>
                </template>
                <template v-else>
                  <slot
                    :name="`input(custom)`"
                    :option="selected_search"
                    :form="search_form"
                  ></slot>
                </template>
              </v-form>
            </validation-observer>
          </template>
          <template v-else>
            <app-form-input
              disabled
              variant="outlined"
              :hide-details="true"
              class="disable-input"
            ></app-form-input>
          </template>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="mt-2 pa-0">
      <v-row no-gutters class="flex-nowrap">
        <div class="d-flex flex-row ga-1 flex-wrap">
          <slot name="tag" :value="search_values">
            <v-chip
              v-for="(search_value, key) in search_values"
              :key="key"
              label
              variant="outlined"
              closable
              @click:close="remove(key)"
            >
              <div class="mr-1">
                <span class="tag-label">{{ searchLabel([key, search]) }}:</span>
              </div>
              <span class="tag-value">
                <slot :name="`tagValue(${key})`" :value="search_value">
                  {{ searchValue([key, search_value, search]) }}
                </slot>
              </span>
            </v-chip>
          </slot>
        </div>
        <v-btn
          v-if="Object.keys(search_values).length > 0"
          class="ml-4 mr-1"
          @click="clear"
          variant="outlined"
        >
          検索条件をクリア
        </v-btn>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import moment from "moment";
import { defineComponent } from "vue";
import type { PropType } from "vue";
import AppFormInput from "@/components/AppFormInput.vue";

export type SearchSelectOption = {
  type: "select";
  label?: string;
  items: { text: string; value: unknown }[];
};
export type SearchOptionValue =
  | { type: "text" | "date" | "custom"; label?: string }
  | SearchSelectOption;
type SearchItem = SearchOptionValue & { key: string };
export type SearchOption = Record<string, SearchOptionValue>;

// 選択肢のnullの代わり値
const searchAppNull = "SearchAppNull";

/** AWSのチックな検索UIを提供してくれるコンポーネント */
export default defineComponent({
  name: "AppSearch",
  components: { AppFormInput },
  props: {
    /**
     * 検索オプション
     * 検索の項目と種類など情報を設定することで任意の検索UIが作成できる
     */
    search: {
      type: Object as PropType<SearchOption>,
      required: true,
    },
    /** 検索条件 */
    modelValue: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
      required: false,
    },
    /** 検索フィームの初期値。1項目に対してオブジェクトを設定したい場合など。主にカスタム用 */
    initialForm: {
      type: Function as PropType<
        () => Record<string, string | number | Object>
      >,
      required: false,
    },
  },
  data() {
    return {
      /** 選択中の検索項目 */
      selected_search: null as SearchItem | null,
      /** 検索項目の入力form */
      search_form: this.initialForm
        ? this.initialForm()
        : ({} as Record<string, string | number>),
      /** 検索条件（タグ） */
      search_values: this.modelValue as Record<string, unknown>,
    };
  },
  computed: {
    /**
     * 検索オプション一覧
     * 既に条件が設定されかつcustomのものは除外される
     */
    searchList(): SearchItem[] {
      const valueKeys = new Set(Object.keys(this.search_values));
      return Object.entries(this.search)
        .filter(
          ([key, value]) =>
            (value.type === "custom" && !valueKeys.has(key)) ||
            value.type !== "custom"
        )
        .map(([key, value]) => ({ key: key, ...value }));
    },

    /** 選択中の検索項目のラベル */
    selectedSearchLabel(): null | string {
      if (this.selected_search) {
        return this.selected_search.label ?? this.selected_search.key;
      } else {
        return null;
      }
    },
    /**
     * select boxの選択肢
     * 選択されたものは非表示となる
     */
    selectItems(): [] | SearchSelectOption["items"] {
      const selectedItems = Object.entries(this.search_values)
        .filter(([key]) => key === this.selected_search?.key)
        .flatMap(([, value]) => (Array.isArray(value) ? [...value] : [value]));
      return (this.selected_search as SearchSelectOption).items
        .filter((v) => !selectedItems.includes(v.value))
        .map((v) => {
          if (v.value === null) {
            // 選択項目はvalueがnullの場合、変換(nullがデフォルト選択されているので)
            return { ...v, value: searchAppNull };
          }
          return v;
        });
    },
  },
  watch: {
    modelValue(newValue: Record<string, unknown>) {
      this.search_values = newValue;
    },
  },
  methods: {
    /** 検索オプションのkeyから項目のラベル文字列に変換 */
    searchLabel([key, search]: [string, SearchOption]): string {
      return Object.entries(search)
        .filter(([searchKey]) => key === searchKey)
        .map(([key, value]) => value.label ?? key)[0];
    },

    /** 検索オプションのkeyと値から値のラベル文字列に変換
     *
     * 同じ項目に複数の選択値が指定した場合、コンマ(, )区切りで一文字列にまとめて返却する
     */
    searchValue([key, value, search]: [string, unknown, SearchOption]): string {
      const targetSearch = Object.entries(search).find(
        ([searchKey]) => key === searchKey
      );
      if (targetSearch) {
        if (Array.isArray(value)) {
          return value
            .map((v) => {
              return targetSearch[1].type === "select"
                ? targetSearch[1].items
                    .filter((e) => e.value === v)
                    .map((e) => e.text)[0]
                : targetSearch[1].type === "date"
                  ? moment(v).format("YYYY年MM月DD日")
                  : v;
            })
            .join(", ");
        } else {
          return targetSearch[1].type === "select"
            ? targetSearch[1].items
                .filter((e) => e.value === value)
                .map((e) => e.text)[0]
            : targetSearch[1].type === "date"
              ? moment(value as string).format("YYYY年MM月DD日")
              : (value as string);
        }
      } else {
        // 通常ここの処理に入ることはない
        throw new Error("cannot convert");
      }
    },
    itemProps(item: SearchItem) {
      return {
        title: item.label ? item.label : item.key,
      };
    },
    /** 検索条件を追加 */
    submit() {
      this.search_values = {
        ...this.search_values,
        ...Object.entries(this.search_form)
          .filter(([key]) => key === this.selected_search!.key)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .filter(([_key, value]) =>
            this.selected_search!.type === "text" ? value !== null : true
          )
          .reduce((acc, [key, value]) => {
            if (this.search_values[key] !== undefined) {
              // 既にある場合
              if (Array.isArray(this.search_values[key])) {
                return {
                  [key]: [
                    ...(this.search_values[key] as []),
                    this.selected_search!.type === "select" &&
                    value === searchAppNull
                      ? null
                      : value,
                  ],
                };
              } else {
                return {
                  [key]: [
                    this.search_values[key],
                    this.selected_search!.type === "select" &&
                    value === searchAppNull
                      ? null
                      : value,
                  ],
                };
              }
            } else {
              // 初めての場合
              // 選択肢のnull値を元に戻す
              return {
                ...acc,
                [key]:
                  this.selected_search!.type === "select" &&
                  value === searchAppNull
                    ? null
                    : value,
              };
            }
          }, {}),
      };
      this.search_form = this.initialForm ? this.initialForm() : {};
      /**
       * v-selectのkeyPressイベント終わらないと、エラーになったので、DOM更新完了まで待ち
       * nextTickではDOM構造が更新されたことを保証されるが、
       * DOMレンダリングが完了することが保証されるわけではなく、高さが取得出来ない
       * そのため、setTimeoutを利用してDOMレンダリングが完了後に検索条件クリアを実施する
       */
      setTimeout(() => {
        this.selected_search = null;
      });
      this.$emit("update:modelValue", this.search_values);
    },

    /** 該当のkeyの検索条件を削除 */
    remove(key: string) {
      delete this.search_values[key];
      this.$emit("update:modelValue", this.search_values);
    },

    /** 全ての検索条件を削除 */
    clear() {
      this.search_values = {};
      this.selected_search = null;
      this.$emit("update:modelValue", this.search_values);
    },
  },
});
</script>

<style lang="scss" scoped>
.search {
  min-height: 85px;

  > .v-container {
    max-width: 100%;
  }

  .v-select {
    min-width: 140px;
    flex: unset;
  }

  .search-form {
    padding: 0;

    input,
    select {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }

  .tag-label {
    font-weight: bold;
  }

  .btn-clear {
    min-width: 170px;
  }

  span.tag-value {
    white-space: initial;
    word-break: break-word;
    text-align: left;
  }

  .v-chip {
    min-height: 32px;
    height: auto;
  }

  .disable-input {
    background-color: #e9ecef;
  }
}
</style>
